import axios from 'axios';

// API_BASE_URL は環境変数から取得（必須）
// 本番/STG/開発環境では .env または docker-compose で REACT_APP_API_URL を設定すること
const API_BASE_URL = process.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  console.error(
    '[API] REACT_APP_API_URL environment variable is not set. ' +
    'Please configure it in your .env file or docker-compose.yml'
  );
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const setAuthToken = token => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

export const getPlans = async () => {
  try {
    const response = await api.get('/plans/');
    return response.data;
  } catch (error) {
    console.error('Failed to get plans:', error);
    throw error;
  }
};

export const createCheckoutSession = async (
  planId,
  isYearly,
  discountedPrice,
) => {
  try {
    const response = await api.post('/subscriptions/create-checkout-session', {
      plan_id: planId,
      is_yearly: isYearly,
      discounted_price: discountedPrice,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create checkout session:', error);
    throw error;
  }
};

export const cancelSubscription = async () => {
  try {
    const response = await api.post('/subscriptions/cancel-subscription');
    return response.data;
  } catch (error) {
    console.error('Failed to cancel subscription:', error);
    throw error;
  }
};

export const getCurrentSubscription = async () => {
  try {
    const response = await api.get('/subscriptions/current-subscription');
    return response.data;
  } catch (error) {
    console.error('Failed to get current subscription:', error);
    throw error;
  }
};

export const createPortalSession = async () => {
  try {
    const response = await api.post('/subscriptions/create-portal-session');
    return response.data;
  } catch (error) {
    console.error('Failed to create portal session:', error);
    throw error;
  }
};

export const calculateProratedAmount = async (
  currentPlanId,
  newPlanId,
  isYearly,
) => {
  try {
    const response = await api.post(
      '/subscriptions/calculate-prorated-amount',
      {
        current_plan_id: currentPlanId,
        new_plan_id: newPlanId,
        is_yearly: isYearly,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Failed to calculate prorated amount:', error);
    throw error;
  }
};

export const cancelPendingPlanChange = async () => {
  try {
    await api.post('/subscriptions/cancel-pending-plan-change');
  } catch (error) {
    console.error('Failed to cancel pending plan change:', error);
  }
};

export const trialApi = {
  startTrial: async (planId, isYearly = false) => {
    const response = await api.post('/subscriptions/start-trial', {
      plan_id: planId,
      is_yearly: isYearly,
    });
    return response.data;
  },
  getTrialStatus: async () => {
    const response = await api.get('/subscriptions/trial-status');
    return response.data;
  },
  checkEligibility: async () => {
    const response = await api.get('/subscriptions/trial-eligibility');
    return response.data;
  },
};

export const getUserAnalytics = async userId => {
  try {
    const response = await api.get(`/users/${userId}/analytics`);
    return response.data;
  } catch (error) {
    console.error('Failed to get user analytics:', error);
    throw error;
  }
};

export const getAdminDashboard = async (startDate, endDate) => {
  try {
    const response = await api.get('/admin/dashboard', {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get admin dashboard data:', error);
    throw error;
  }
};

export const recordActivity = async activityType => {
  try {
    await api.post('/users/record-activity', { activity_type: activityType });
  } catch (error) {
    console.error('Failed to record activity:', error);
  }
};

// =============================================================================
// V2 SEO記事生成モード API クライアント
// =============================================================================

// V2 独自情報管理API
export const v2ToppingsApi = {
  // 独自情報一覧取得
  getToppings: (params = {}) => api.get('/api/v2/toppings/', { params }),

  // 独自情報詳細取得
  getTopping: id => api.get(`/api/v2/toppings/${id}`),

  // 独自情報作成
  createTopping: data => api.post('/api/v2/toppings/', data),

  // URLから独自情報作成
  createToppingFromUrl: data => api.post('/api/v2/toppings/import/url', data),

  // ファイルアップロード
  uploadTopping: formData =>
    api.post('/api/v2/toppings/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // 独自情報更新
  updateTopping: (id, data) => api.put(`/api/v2/toppings/${id}`, data),

  // 独自情報削除
  deleteTopping: id => api.delete(`/api/v2/toppings/${id}`),

  // 統計情報取得
  getToppingsStats: () => api.get('/api/v2/toppings/stats'),

  // ユニット使用状況取得
  getToppingUsage: () => api.get('/api/v2/toppings/usage'),

  // ユニット見積もり
  estimateUnits: formData =>
    api.post('/api/v2/toppings/estimate-units', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // 検索機能
  searchToppings: params => api.get('/api/v2/toppings/search', { params }),

  // カテゴリー一覧取得
  getCategories: () => api.get('/api/v2/toppings/categories'),

  // ユーザータグ一覧取得
  getTags: () => api.get('/api/v2/toppings/tags'),

  // プレビュー付きファイルアップロード（DB保存なし）
  uploadWithPreview: formData =>
    api.post('/api/v2/toppings/advanced/upload-with-preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // URL抽出（FireCrawl）
  extractUrls: data => api.post('/api/v2/toppings/advanced/extract-urls', data),

  // ファイル再アップロード
  reuploadTopping: (id, formData) =>
    api.post(`/api/v2/toppings/${id}/reupload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

// V2 プロンプト管理API
export const v2PromptsApi = {
  // プロンプト一覧取得
  getPrompts: async (params = {}) => {
    try {
      const response = await api.get('/api/v2/prompts/', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to get prompts:', error);
      throw error;
    }
  },

  // プロンプト詳細取得
  getPrompt: async promptId => {
    try {
      const response = await api.get(`/api/v2/prompts/${promptId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get prompt:', error);
      throw error;
    }
  },

  // プロンプト作成
  createPrompt: async promptData => {
    try {
      const response = await api.post('/api/v2/prompts/', promptData);
      return response.data;
    } catch (error) {
      console.error('Failed to create prompt:', error);
      throw error;
    }
  },

  // プロンプト更新
  updatePrompt: async (promptId, promptData) => {
    try {
      const response = await api.put(`/api/v2/prompts/${promptId}`, promptData);
      return response.data;
    } catch (error) {
      console.error('Failed to update prompt:', error);
      throw error;
    }
  },

  // モデルごとの thinking / effort 対応表を取得
  // 対応表はバックエンドが唯一の正典（FEに持つと必ず乖離するため）
  getModelCapabilities: async () => {
    try {
      const response = await api.get('/api/v2/prompts/model-capabilities');
      return response.data;
    } catch (error) {
      console.error('Failed to get model capabilities:', error);
      // 取得できなくても編集自体は続けられるよう、空の対応表で縮退する
      return { thinking_choices: [], models: {} };
    }
  },

  // プロンプト削除
  deletePrompt: async promptId => {
    try {
      const response = await api.delete(`/api/v2/prompts/${promptId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete prompt:', error);
      throw error;
    }
  },

  // 新バージョン作成
  createVersion: async (promptId, versionData) => {
    try {
      const response = await api.post(
        `/api/v2/prompts/${promptId}/versions`,
        versionData,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create version:', error);
      throw error;
    }
  },

  // バージョン切り替え
  activateVersion: async (promptId, version) => {
    try {
      const response = await api.put(
        `/api/v2/prompts/${promptId}/versions/${version}/activate`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to activate version:', error);
      throw error;
    }
  },

  // バージョン削除
  deleteVersion: async (promptId, version) => {
    try {
      const response = await api.delete(
        `/api/v2/prompts/${promptId}/versions/${version}`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to delete version:', error);
      throw error;
    }
  },

  // テスト実行
  testPromptExecution: async (promptId, testData) => {
    try {
      const response = await api.post(
        `/api/v2/prompts/${promptId}/test`,
        testData,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to test prompt execution:', error);
      throw error;
    }
  },

  // テスト実行（エイリアス）
  testPrompt: async (promptId, testData) => {
    try {
      const response = await api.post(
        `/api/v2/prompts/${promptId}/test`,
        testData,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to test prompt:', error);
      throw error;
    }
  },

  // 分析データ一覧取得
  getAnalysisDataList: async () => {
    try {
      const response = await api.get('/api/v2/prompts/analysis-data');
      return response.data;
    } catch (error) {
      console.error('Failed to get analysis data list:', error);
      throw error;
    }
  },

  // プロンプト初期化
  initializePrompts: async () => {
    try {
      const response = await api.post('/api/v2/prompts/initialize');
      return response.data;
    } catch (error) {
      console.error('Failed to initialize prompts:', error);
      throw error;
    }
  },

  // プロンプト初期化（エイリアス）
  initialize: async () => {
    try {
      const response = await api.post('/api/v2/prompts/initialize');
      return response.data;
    } catch (error) {
      console.error('Failed to initialize prompts:', error);
      throw error;
    }
  },

  // ファクトチェック用プロンプト初期化
  initializeFactCheckPrompts: async (forceUpdate = false) => {
    try {
      const response = await api.post(
        `/api/v2/prompts/initialize-fact-check?force_update=${forceUpdate}`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to initialize fact check prompts:', error);
      throw error;
    }
  },

  // 分析結果からプロンプト用変数を取得
  getAnalysisPromptVariables: async analysisId => {
    try {
      const response = await api.get(
        `/api/v2/prompts/step1-analysis/${analysisId}/prompt-variables`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get analysis prompt variables:', error);
      throw error;
    }
  },

  // スタイルからプロンプト用変数を取得
  getStylePromptFormat: async styleId => {
    try {
      const response = await api.get(
        `/api/v2/prompts/styles/${styleId}/prompt-format`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get style prompt format:', error);
      throw error;
    }
  },
};

// 記事生成API（将来的に実装）
export const v2ArticlesApi = {
  // 記事一覧取得
  getArticles: async (params = {}) => {
    try {
      const requestParams = { ...params };
      // my_only パラメータを明示的に追加
      if (params.my_only) {
        requestParams.my_only = true;
      }
      const response = await api.get('/api/v2/articles/', {
        params: requestParams,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get articles:', error);
      throw error;
    }
  },

  // 記事統計取得（最適化版：全件取得せずに統計のみ取得）
  getArticleStats: async () => {
    try {
      const response = await api.get('/api/v2/articles/stats/summary');
      return response.data;
    } catch (error) {
      console.error('Failed to get article stats:', error);
      throw error;
    }
  },

  // 記事詳細取得
  getArticle: async uuid => {
    try {
      const response = await api.get(`/api/v2/articles/${uuid}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get article:', error);
      throw error;
    }
  },

  // 記事作成
  createArticle: async articleData => {
    try {
      const response = await api.post('/api/v2/articles/', articleData);
      return response.data;
    } catch (error) {
      console.error('Failed to create article:', error);
      throw error;
    }
  },

  // アウトライン生成の再実行
  retryOutlineGeneration: async uuid => {
    try {
      const response = await api.post(`/api/v2/articles/${uuid}/outline/retry`);
      return response.data;
    } catch (error) {
      console.error('Failed to retry outline generation:', error);
      throw error;
    }
  },

  // ベースアウトライン選択確定
  selectOutline: async (uuid, selectedId, additionalRequest) => {
    try {
      const response = await api.post(
        `/api/v2/articles/${uuid}/outline/select`,
        { selected_id: selectedId, additional_request: additionalRequest },
      );
      return response.data;
    } catch (error) {
      console.error('Failed to select outline:', error);
      throw error;
    }
  },

  // 記事更新
  updateArticle: async (uuid, updateData) => {
    try {
      const response = await api.put(`/api/v2/articles/${uuid}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Failed to update article:', error);
      throw error;
    }
  },

  // 記事削除
  deleteArticle: async uuid => {
    try {
      const response = await api.delete(`/api/v2/articles/${uuid}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete article:', error);
      throw error;
    }
  },

  // タグ追加
  addTag: async (uuid, tag) => {
    try {
      const response = await api.post(`/api/v2/articles/${uuid}/tags`, { tag });
      return response.data;
    } catch (error) {
      console.error('Failed to add tag:', error);
      throw error;
    }
  },

  // タグ削除
  removeTag: async (uuid, tag) => {
    try {
      const response = await api.delete(`/api/v2/articles/${uuid}/tags/${encodeURIComponent(tag)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to remove tag:', error);
      throw error;
    }
  },

  // 記事複製
  copyArticle: async (uuid, newKeyword = null) => {
    try {
      const response = await api.post(`/api/v2/articles/${uuid}/copy`, {
        keyword: newKeyword,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to copy article:', error);
      throw error;
    }
  },

  // 記事再生成
  regenerateArticle: async uuid => {
    try {
      const response = await api.post(`/api/v2/articles/${uuid}/regenerate`);
      return response.data;
    } catch (error) {
      console.error('Failed to regenerate article:', error);
      throw error;
    }
  },

  // スコアリング実行
  startScoring: async (uuid, outlineData) => {
    try {
      const response = await api.post(`/api/v2/articles/${uuid}/scoring`, {
        outline_data: outlineData,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to start scoring:', error);
      throw error;
    }
  },

  // スコアリング結果取得
  getScoring: async uuid => {
    try {
      const response = await api.get(`/api/v2/articles/${uuid}/scoring`);
      return response.data;
    } catch (error) {
      console.error('Failed to get scoring:', error);
      throw error;
    }
  },

  // アクションプランのチェック状態を更新
  updateActionPlanCheck: async (uuid, actionId, checked) => {
    try {
      const response = await api.patch(
        `/api/v2/articles/${uuid}/scoring/action-plan-check`,
        {
          action_id: actionId,
          checked: checked,
        },
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update action plan check:', error);
      throw error;
    }
  },

  // STEP3 一時保存（working_draft）
  saveOutlineDraft: async (uuid, draft) => {
    try {
      const response = await api.post(
        `/api/v2/articles/${uuid}/outline/draft`,
        draft,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to save outline draft:', error);
      throw error;
    }
  },

  // STEP3→STEP2に戻る（working_draftをクリア）
  resetToCandidates: async uuid => {
    try {
      const response = await api.post(
        `/api/v2/articles/${uuid}/outline/reset-to-candidates`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to reset to candidates:', error);
      throw error;
    }
  },

  // STEP4.1: 執筆計画生成
  generateWritingPlan: async uuid => {
    try {
      const response = await api.post(
        `/api/v2/articles/${uuid}/step4/writing-plan/generate`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to generate writing plan:', error);
      throw error;
    }
  },

  // STEP4.1: 執筆プラン取得
  getWritingPlan: async uuid => {
    try {
      const response = await api.get(
        `/api/v2/articles/${uuid}/step4/writing-plan`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get writing plan:', error);
      throw error;
    }
  },

  // STEP4.1: 執筆プラン更新（一時保存）
  saveWritingPlan: async (uuid, updatedPlans) => {
    try {
      const response = await api.put(
        `/api/v2/articles/${uuid}/step4/writing-plan`,
        updatedPlans,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to save writing plan:', error);
      throw error;
    }
  },

  // STEP4.2: 本文生成開始
  generateContent: async (uuid, options = {}) => {
    try {
      const response = await api.post(
        `/api/v2/articles/${uuid}/step4/content/generate`,
        options,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to generate content:', error);
      throw error;
    }
  },

  // STEP4.2: 失敗セクションの再生成
  retrySection: async (uuid, headingId) => {
    try {
      const response = await api.post(
        `/api/v2/articles/${uuid}/step4/content/retry-section/${headingId}`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to retry section:', error);
      throw error;
    }
  },

  // STEP4.2: 生成された本文を取得
  getGeneratedContent: async uuid => {
    try {
      const response = await api.get(`/api/v2/articles/${uuid}/step4/content`);
      return response.data;
    } catch (error) {
      console.error('Failed to get generated content:', error);
      throw error;
    }
  },

  // STEP4.2: 本文生成の進捗を取得
  getContentGenerationProgress: async uuid => {
    try {
      const response = await api.get(
        `/api/v2/articles/${uuid}/step4/content/progress`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get content generation progress:', error);
      throw error;
    }
  },

  // STEP4.3: ブラッシュアップを実行
  executeRefinement: async (uuid, options = {}) => {
    try {
      const response = await api.post(
        `/api/v2/articles/${uuid}/step4/refinement`,
        options,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to execute refinement:', error);
      throw error;
    }
  },

  // STEP4.3: ブラッシュアップ履歴を取得
  getRefinementHistory: async uuid => {
    try {
      const response = await api.get(
        `/api/v2/articles/${uuid}/step4/refinement-history`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get refinement history:', error);
      throw error;
    }
  },

  // STEP4.3: 特定バージョンのブラッシュアップ結果を取得
  getRefinementVersion: async (uuid, version) => {
    try {
      const response = await api.get(
        `/api/v2/articles/${uuid}/step4/refinement/${version}`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get refinement version:', error);
      throw error;
    }
  },

  // STEP4.3: ブラッシュアップバージョンを確定してSTEP4-4へ
  confirmRefinementVersion: async (uuid, version) => {
    try {
      const response = await api.post(
        `/api/v2/articles/${uuid}/step4/refinement/confirm`,
        { version },
      );
      return response.data;
    } catch (error) {
      console.error('Failed to confirm refinement version:', error);
      throw error;
    }
  },

  // STEP4-4: 最終調整ドラフト取得
  getStep4Draft: async uuid => {
    try {
      const response = await api.get(`/api/v2/articles/${uuid}/step4/draft`);
      return response.data;
    } catch (error) {
      console.error('Failed to get step4 draft:', error);
      throw error;
    }
  },

  // STEP4: 参考データ（citations）取得
  getStep4Citations: async uuid => {
    try {
      const response = await api.get(
        `/api/v2/articles/${uuid}/step4/citations`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get step4 citations:', error);
      throw error;
    }
  },

  // STEP4-4: 最終調整ドラフト更新（自動保存）
  updateStep4Draft: async (uuid, draftData) => {
    try {
      const response = await api.post(
        `/api/v2/articles/${uuid}/step4/draft`,
        draftData,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update step4 draft:', error);
      throw error;
    }
  },

  // STEP4.4到達時にEXPを付与
  arriveStep44: async uuid => {
    try {
      const response = await api.post(`/api/v2/articles/${uuid}/step4/arrive`);
      return response.data;
    } catch (error) {
      console.error('Failed to call arrive step4.4:', error);
      throw error;
    }
  },

  // 原稿を確定する（status='completed'に更新）
  finalizeArticle: async uuid => {
    try {
      const response = await api.post(`/api/v2/articles/${uuid}/finalize`);
      return response.data;
    } catch (error) {
      console.error('Failed to finalize article:', error);
      throw error;
    }
  },

  // Word出力
  exportToWord: async uuid => {
    try {
      const response = await api.post(
        `/api/v2/export/${uuid}/word`,
        {},
        {
          responseType: 'blob',
        },
      );
      return response;
    } catch (error) {
      console.error('Failed to export to Word:', error);
      throw error;
    }
  },

  // current_stepを更新
  updateCurrentStep: async (uuid, step) => {
    try {
      const response = await api.patch(
        `/api/v2/articles/${uuid}/current-step`,
        {
          current_step: step,
        },
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update current step:', error);
      throw error;
    }
  },
};

// Pexels画像検索API
export const pexelsApi = {
  // 画像検索
  searchPhotos: async (
    query,
    orientation = 'landscape',
    perPage = 20,
    page = 1,
  ) => {
    try {
      const response = await api.get('/api/v2/pexels/search', {
        params: {
          query,
          orientation,
          per_page: perPage,
          page,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search photos:', error);
      throw error;
    }
  },

  // キュレーション画像取得
  getCuratedPhotos: async (perPage = 20, page = 1) => {
    try {
      const response = await api.get('/api/v2/pexels/curated', {
        params: {
          per_page: perPage,
          page,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get curated photos:', error);
      throw error;
    }
  },
};

// 🔧 進行中のリクエストを追跡（重複防止）
const ongoingRequests = new Map();

// 新しい分析管理API（分離アーキテクチャ）
export const v2AnalysisApi = {
  // 分析開始（バックグラウンドキューに追加）
  startAnalysis: async analysisData => {
    try {
      // 🔧 重複リクエスト防止
      const requestKey = `start_analysis_${analysisData.keyword}_${analysisData.location}`;

      if (ongoingRequests.has(requestKey)) {
        console.log(
          '🔧 [DUPLICATE_PREVENTION] Request already in progress:',
          requestKey,
        );
        return await ongoingRequests.get(requestKey);
      }

      // リクエストを開始
      const requestPromise = api.post('/api/v2/step1/analyze', analysisData);
      ongoingRequests.set(requestKey, requestPromise);

      try {
        const response = await requestPromise;
        ongoingRequests.delete(requestKey);
        return response.data;
      } catch (error) {
        ongoingRequests.delete(requestKey);
        throw error;
      }
    } catch (error) {
      console.error('Failed to start analysis:', error);
      throw error;
    }
  },

  // 分析状況取得
  getAnalysisStatus: async analysisId => {
    console.log('🚨 getAnalysisStatus called with analysisId:', analysisId);
    console.trace('Call stack:');
    try {
      const response = await api.get(`/api/v2/step1/${analysisId}/status`);
      return response.data;
    } catch (error) {
      console.error('Failed to get analysis status:', error);
      throw error;
    }
  },

  // 分析結果取得
  getAnalysisResult: async analysisId => {
    try {
      const response = await api.get(`/api/v2/step1/${analysisId}/results`);
      return response.data;
    } catch (error) {
      console.error('Failed to get analysis result:', error);
      throw error;
    }
  },

  // 分析履歴一覧取得
  listAnalyses: async (
    params = {
      skip: 0,
      limit: 20,
      status: undefined,
      q: undefined,
      user_id: undefined,
      my_only: false,
      tag: undefined,
    },
  ) => {
    try {
      const {
        skip = 0,
        limit = 20,
        status,
        q,
        user_id,
        my_only,
        tag,
      } = params || {};
      const requestParams = {
        skip,
        limit,
        status_filter: status,
        keyword_search: q,
      };
      // 管理者用：user_idが指定されている場合のみ追加
      if (user_id) {
        requestParams.user_id = user_id;
      }
      // 管理者でも自分のリサーチのみ取得する場合
      if (my_only) {
        requestParams.my_only = true;
      }
      // タグフィルタ
      if (tag) {
        requestParams.tag = tag;
      }
      const response = await api.get('/api/v2/analysis/list', {
        params: requestParams,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to list analyses:', error);
      throw error;
    }
  },

  // 軽量ステータス一覧（未完了のみ）
  listStatusLite: async () => {
    try {
      const response = await api.get('/api/v2/analysis/list/status-lite');
      return response.data;
    } catch (error) {
      console.error('Failed to list status-lite:', error);
      throw error;
    }
  },

  // アクティブ（待機中・実行中）分析一覧
  getActiveAnalyses: async () => {
    try {
      const response = await api.get('/api/v2/analysis/active');
      return response.data;
    } catch (error) {
      console.error('Failed to get active analyses:', error);
      throw error;
    }
  },

  // 分析キャンセル（待機中のみ）
  cancelAnalysis: async analysisId => {
    try {
      const response = await api.post(`/api/v2/analysis/${analysisId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Failed to cancel analysis:', error);
      throw error;
    }
  },

  // 分析結果削除
  deleteAnalysis: async analysisId => {
    try {
      const response = await api.delete(`/api/v2/step1/results/${analysisId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete analysis:', error);
      throw error;
    }
  },

  // タグ追加
  addTag: async (analysisId, tag) => {
    try {
      const response = await api.post(`/api/v2/analysis/${analysisId}/tags`, { tag });
      return response.data;
    } catch (error) {
      console.error('Failed to add tag:', error);
      throw error;
    }
  },

  // タグ削除
  removeTag: async (analysisId, tag) => {
    try {
      const response = await api.delete(`/api/v2/analysis/${analysisId}/tags/${encodeURIComponent(tag)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to remove tag:', error);
      throw error;
    }
  },

  // 分析結果から記事生成開始
  startGenerationFromAnalysis: async (analysisId, generationData) => {
    try {
      const response = await api.post('/api/v2/generation/from-analysis', {
        step1_analysis_id: analysisId,
        ...generationData,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to start generation from analysis:', error);
      throw error;
    }
  },

  // 共有設定更新
  setShareStatus: async (analysisId, shareData) => {
    try {
      const response = await api.post(
        `/api/v2/step1/analysis/${analysisId}/share`,
        shareData,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to set share status:', error);
      throw error;
    }
  },

  // 共有分析結果取得
  getSharedAnalysis: async (analysisId, password = null) => {
    try {
      const params = password ? { password } : {};
      const response = await api.get(
        `/api/v2/step1/shared/analysis/${analysisId}`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get shared analysis:', error);
      throw error;
    }
  },
};

// ステップ①SEO/AIO分析API（レガシー）
export const v2Step1AnalysisApi = {
  // 分析開始
  startAnalysis: async analysisData => {
    try {
      const response = await api.post('/api/v2/step1/analyze', analysisData);
      return response.data;
    } catch (error) {
      console.error('Failed to start step1 analysis:', error);
      throw error;
    }
  },

  // 分析状況取得
  getAnalysisStatus: async analysisId => {
    try {
      const response = await api.get(`/api/v2/step1/status/${analysisId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get analysis status:', error);
      throw error;
    }
  },

  // 分析結果取得
  getAnalysisResults: async analysisId => {
    try {
      const response = await api.get(`/api/v2/step1/results/${analysisId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get analysis results:', error);
      throw error;
    }
  },

  // 簡易分析（同期実行）
  quickAnalyze: async analysisData => {
    try {
      // デフォルトでLLMをスキップ（開発時の高速化のため）
      const dataWithDefaults = {
        ...analysisData,
        skip_llm:
          analysisData.skip_llm !== undefined ? analysisData.skip_llm : true,
      };
      const response = await api.post(
        '/api/v2/step1/quick-analyze',
        dataWithDefaults,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to perform quick analysis:', error);
      throw error;
    }
  },

  // ユーザーの分析履歴取得
  getUserAnalyses: async () => {
    try {
      const response = await api.get('/api/v2/step1/user-analyses');
      return response.data;
    } catch (error) {
      console.error('Failed to get user analyses:', error);
      throw error;
    }
  },

  // 分析結果削除
  deleteAnalysis: async analysisId => {
    try {
      const response = await api.delete(`/api/v2/step1/results/${analysisId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete analysis:', error);
      throw error;
    }
  },
};

// スタイル管理API（将来的に実装）
export const v2StylesApi = {
  // スタイル一覧取得
  getStyles: async (params = {}) => {
    try {
      const response = await api.get('/api/v2/styles/', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to get styles:', error);
      throw error;
    }
  },

  // スタイル作成
  createStyle: async styleData => {
    try {
      const response = await api.post('/api/v2/styles/', styleData);
      return response.data;
    } catch (error) {
      console.error('Failed to create style:', error);
      throw error;
    }
  },

  // スタイル解析（URLまたはテキスト）
  analyzeStyle: async payload => {
    try {
      const response = await api.post('/api/v2/styles/analyze', payload);
      return response.data;
    } catch (error) {
      console.error('Failed to analyze style:', error);
      throw error;
    }
  },

  // スタイル解析プレビュー（保存しない）- キュー登録
  analyzeStylePreview: async payload => {
    try {
      const response = await api.post(
        '/api/v2/styles/analyze-preview',
        payload,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to preview analyze style:', error);
      throw error;
    }
  },

  // スタイル解析ステータス取得（ポーリング用）
  getStyleAnalysisStatus: async jobId => {
    try {
      const response = await api.get(
        `/api/v2/styles/analyze-preview/${jobId}/status`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get style analysis status:', error);
      throw error;
    }
  },

  // スタイル更新
  updateStyle: async (styleId, updateData) => {
    try {
      const response = await api.put(`/api/v2/styles/${styleId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Failed to update style:', error);
      throw error;
    }
  },

  // スタイル削除
  deleteStyle: async styleId => {
    try {
      const response = await api.delete(`/api/v2/styles/${styleId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete style:', error);
      throw error;
    }
  },

  // スタイルエクスポート（json|yaml）
  exportStyle: async (styleId, fmt = 'json') => {
    try {
      const response = await api.get(`/api/v2/styles/${styleId}/export`, {
        params: { fmt },
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to export style:', error);
      throw error;
    }
  },

  // URLから本文インポート（保存しない）
  importFromUrl: async source_url => {
    try {
      const response = await api.post('/api/v2/styles/import', { source_url });
      return response.data;
    } catch (error) {
      console.error('Failed to import article:', error);
      throw error;
    }
  },

  // JSON→YAML 変換（保存しない）
  convertYaml: async styleConfig => {
    try {
      const response = await api.post('/api/v2/styles/convert-yaml', {
        style_config: styleConfig,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to convert yaml:', error);
      throw error;
    }
  },
};

// システムアップデート情報API
export const v2SystemUpdatesApi = {
  // アップデート情報一覧取得
  getUpdates: async (params = {}) => {
    try {
      const { limit = 10, include_unpublished = false } = params;
      const response = await api.get('/api/v2/system-updates/', {
        params: { limit, include_unpublished },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get system updates:', error);
      throw error;
    }
  },

  // アップデート情報作成（masterのみ）
  createUpdate: async updateData => {
    try {
      const response = await api.post('/api/v2/system-updates/', updateData);
      return response.data;
    } catch (error) {
      console.error('Failed to create system update:', error);
      throw error;
    }
  },

  // アップデート情報更新（masterのみ）
  updateUpdate: async (updateId, updateData) => {
    try {
      const response = await api.put(
        `/api/v2/system-updates/${updateId}`,
        updateData,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update system update:', error);
      throw error;
    }
  },

  // アップデート情報削除（masterのみ）
  deleteUpdate: async updateId => {
    try {
      const response = await api.delete(`/api/v2/system-updates/${updateId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete system update:', error);
      throw error;
    }
  },
};

// ゲーミフィケーションAPI
export const gamificationApi = {
  // ゲーミフィケーションステータス取得
  getStatus: async () => {
    try {
      const response = await api.get('/api/gamification/status');
      return response.data;
    } catch (error) {
      console.error('Failed to get gamification status:', error);
      throw error;
    }
  },

  // 毎日ログイン記録
  recordDailyLogin: async () => {
    try {
      const response = await api.post('/api/gamification/daily-login');
      return response.data;
    } catch (error) {
      console.error('Failed to record daily login:', error);
      throw error;
    }
  },

  // ランクアップ通知済みをマーク
  markRankNotified: async () => {
    try {
      const response = await api.post('/api/gamification/rank-notified');
      return response.data;
    } catch (error) {
      console.error('Failed to mark rank as notified:', error);
      throw error;
    }
  },

  // チュートリアル完了/サーベイ送信
  submitTutorialSurvey: async surveyData => {
    try {
      const response = await api.post(
        '/api/gamification/tutorial/survey',
        surveyData,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to submit tutorial survey:', error);
      throw error;
    }
  },

  // EXP取得履歴
  getExpHistory: async (limit = 50) => {
    try {
      const response = await api.get('/api/gamification/exp-history', {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get EXP history:', error);
      throw error;
    }
  },

  // 機能アンロック状態取得
  getFeaturesStatus: async () => {
    try {
      const response = await api.get('/api/gamification/features');
      return response.data;
    } catch (error) {
      console.error('Failed to get features status:', error);
      throw error;
    }
  },

  // エイリアス（互換性のため）
  getAllFeaturesStatus: async () => {
    try {
      const response = await api.get('/api/gamification/features');
      return response.data;
    } catch (error) {
      // 401エラーは認証前の呼び出しのため抑制
      if (error?.response?.status !== 401) {
        console.error('Failed to get features status:', error);
      }
      throw error;
    }
  },

  // チュートリアルツアー完了状態取得
  getTutorialTours: async () => {
    try {
      const response = await api.get('/api/gamification/tutorial-tours');
      return response.data;
    } catch (error) {
      console.error('Failed to get tutorial tours:', error);
      throw error;
    }
  },

  // チュートリアルツアー完了状態更新
  // チュートリアルツアー状態更新
  updateTutorialTour: async data => {
    try {
      const response = await api.post('/api/gamification/tutorial-tours', data);
      return response.data;
    } catch (error) {
      console.error('Failed to update tutorial tour:', error);
      throw error;
    }
  },
};

// 紹介システムAPI
export const referralApi = {
  // 紹介情報取得
  getInfo: async () => {
    try {
      const response = await api.get('/api/referral/info');
      return response.data;
    } catch (error) {
      console.error('Failed to get referral info:', error);
      throw error;
    }
  },

  // 紹介統計取得
  getStats: async () => {
    try {
      const response = await api.get('/api/referral/stats');
      return response.data;
    } catch (error) {
      console.error('Failed to get referral stats:', error);
      throw error;
    }
  },

  // 紹介コードをトラッキング
  trackReferral: async (referralCode, utmSource = null) => {
    try {
      const response = await api.post('/api/referral/track', {
        referral_code: referralCode,
        utm_source: utmSource,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to track referral:', error);
      throw error;
    }
  },

  // 紹介コード検証
  validateCode: async referralCode => {
    try {
      const response = await api.post('/api/referral/validate-code', null, {
        params: { referral_code: referralCode },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to validate referral code:', error);
      throw error;
    }
  },

  // 紹介者情報取得
  getMyReferrer: async () => {
    try {
      const response = await api.get('/api/referral/my-referrer');
      return response.data;
    } catch (error) {
      console.error('Failed to get my referrer:', error);
      throw error;
    }
  },
};

// =============================================================================
// メールマーケティングAPI（管理者用）
// =============================================================================

export const emailMarketingApi = {
  // --- プリセット ---
  getPresets: async () => {
    const response = await api.get('/admin/email-marketing/presets');
    return response.data;
  },

  // --- ターゲティング ---
  previewTargeting: async (
    filters,
    preset_name = null,
    limit = 100,
    offset = 0,
  ) => {
    const response = await api.post(
      '/admin/email-marketing/targeting/preview',
      {
        filters,
        preset_name,
        limit,
        offset,
      },
    );
    return response.data;
  },

  getTargetingCount: async (preset_name = null) => {
    const params = preset_name ? { preset_name } : {};
    const response = await api.get('/admin/email-marketing/targeting/count', {
      params,
    });
    return response.data;
  },

  // --- キャンペーン ---
  getCampaigns: async (status = null, skip = 0, limit = 20) => {
    const params = { skip, limit };
    if (status) params.status = status;
    const response = await api.get('/admin/email-marketing/campaigns', {
      params,
    });
    return response.data;
  },

  getCampaign: async campaignId => {
    const response = await api.get(
      `/admin/email-marketing/campaigns/${campaignId}`,
    );
    return response.data;
  },

  createCampaign: async data => {
    const response = await api.post('/admin/email-marketing/campaigns', data);
    return response.data;
  },

  updateCampaign: async (campaignId, data) => {
    const response = await api.put(
      `/admin/email-marketing/campaigns/${campaignId}`,
      data,
    );
    return response.data;
  },

  deleteCampaign: async campaignId => {
    const response = await api.delete(
      `/admin/email-marketing/campaigns/${campaignId}`,
    );
    return response.data;
  },

  sendCampaign: async (campaignId, testMode = false, testEmails = []) => {
    const response = await api.post(
      `/admin/email-marketing/campaigns/${campaignId}/send`,
      {
        test_mode: testMode,
        test_emails: testEmails,
      },
    );
    return response.data;
  },

  getCampaignRecipients: async (
    campaignId,
    status = null,
    skip = 0,
    limit = 100,
  ) => {
    const params = { skip, limit };
    if (status) params.status = status;
    const response = await api.get(
      `/admin/email-marketing/campaigns/${campaignId}/recipients`,
      { params },
    );
    return response.data;
  },

  // --- テンプレート ---
  getTemplates: async (category = null, activeOnly = true) => {
    const params = { active_only: activeOnly };
    if (category) params.category = category;
    const response = await api.get('/admin/email-marketing/templates', {
      params,
    });
    return response.data;
  },

  getTemplate: async templateId => {
    const response = await api.get(
      `/admin/email-marketing/templates/${templateId}`,
    );
    return response.data;
  },

  createTemplate: async data => {
    const response = await api.post('/admin/email-marketing/templates', data);
    return response.data;
  },

  updateTemplate: async (templateId, data) => {
    const response = await api.put(
      `/admin/email-marketing/templates/${templateId}`,
      data,
    );
    return response.data;
  },

  deleteTemplate: async templateId => {
    const response = await api.delete(
      `/admin/email-marketing/templates/${templateId}`,
    );
    return response.data;
  },

  // --- 画像 ---
  getImages: async (skip = 0, limit = 50, search = null) => {
    const params = { skip, limit };
    if (search) params.search = search;
    const response = await api.get('/admin/email-marketing/images', { params });
    return response.data;
  },

  uploadImage: async (file, altText = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (altText) formData.append('alt_text', altText);

    const response = await api.post(
      '/admin/email-marketing/images/upload',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return response.data;
  },

  deleteImage: async imageId => {
    const response = await api.delete(
      `/admin/email-marketing/images/${imageId}`,
    );
    return response.data;
  },

  // --- テスト送信 ---
  sendTestEmail: async (subject, htmlContent, testEmail) => {
    const response = await api.post('/admin/email-marketing/test-send', null, {
      params: { subject, html_content: htmlContent, test_email: testEmail },
    });
    return response.data;
  },

  // --- AI機能 ---
  generateEmail: async (
    purpose,
    tone = 'neutral',
    targetSegment = null,
    additionalInstructions = null,
  ) => {
    const response = await api.post('/admin/email-marketing/ai/generate', {
      purpose,
      tone,
      target_segment: targetSegment,
      additional_instructions: additionalInstructions,
    });
    return response.data;
  },

  rewriteEmail: async (
    originalSubject,
    originalContent,
    directions,
    customInstruction = null,
  ) => {
    const response = await api.post('/admin/email-marketing/ai/rewrite', {
      original_subject: originalSubject,
      original_content: originalContent,
      directions,
      custom_instruction: customInstruction,
    });
    return response.data;
  },

  // --- 効果測定 ---
  measureCampaignEffects: async campaignId => {
    const response = await api.post(
      `/admin/email-marketing/campaigns/${campaignId}/measure-effects`,
    );
    return response.data;
  },

  // --- 分析ダッシュボード ---
  getAnalyticsSummary: async (days = 30, campaignId = null) => {
    const params = { days };
    if (campaignId) params.campaign_id = campaignId;
    const response = await api.get('/admin/email-marketing/analytics/summary', {
      params,
    });
    return response.data;
  },

  getAnalyticsTrends: async (days = 30, campaignId = null) => {
    const params = { days };
    if (campaignId) params.campaign_id = campaignId;
    const response = await api.get('/admin/email-marketing/analytics/trends', {
      params,
    });
    return response.data;
  },

  getSegmentPerformance: async (days = 30) => {
    const response = await api.get(
      '/admin/email-marketing/analytics/segment-performance',
      { params: { days } },
    );
    return response.data;
  },

  getTopCampaigns: async (days = 30, limit = 10, sortBy = 'open_rate') => {
    const response = await api.get(
      '/admin/email-marketing/analytics/top-campaigns',
      {
        params: { days, limit, sort_by: sortBy },
      },
    );
    return response.data;
  },
};

// クレジット管理API
export const creditsApi = {
  /**
   * 自分のクレジット情報を取得
   * @returns {Promise<Object>} クレジット情報
   */
  getMyCredits: async () => {
    try {
      const response = await api.get('/users/me/credits');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch credit info:', error);
      throw error;
    }
  },

  /**
   * 指定ユーザーにボーナスクレジットを付与（管理者専用）
   * @param {string} userId - 対象ユーザーID
   * @param {number} amount - 付与するクレジット数
   * @param {string} reason - 付与理由
   * @returns {Promise<Object>} 付与結果とクレジット情報
   */
  addBonusCredits: async (userId, amount, reason) => {
    try {
      const response = await api.post(`/users/${userId}/credits/bonus`, {
        amount,
        reason,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to add bonus credits:', error);
      throw error;
    }
  },

  /**
   * 指定ユーザーのクレジット使用量をリセット（管理者専用）
   * @param {string} userId - 対象ユーザーID
   * @param {string} reason - リセット理由（任意）
   * @returns {Promise<Object>} リセット結果とクレジット情報
   */
  resetCredits: async (userId, reason = 'Manual reset by admin') => {
    try {
      const response = await api.post(`/users/${userId}/credits/reset`, {
        reason,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to reset credits:', error);
      throw error;
    }
  },
};

export const publicSamplesApi = {
  listSamples: async () => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples`);
    return response.data;
  },
  getSampleResearch: async (sampleId) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/${sampleId}/research`);
    return response.data;
  },
  getSampleArticle: async (sampleId) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/${sampleId}/article`);
    return response.data;
  },
  getSampleAnalysisList: async () => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/analysis-list`);
    return response.data;
  },
  getSampleArticleList: async () => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/article-list`);
    return response.data;
  },
  getAnalysisStatus: async (analysisId) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/analysis/${analysisId}/status`);
    return response.data;
  },
  getAnalysisResult: async (analysisId) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/analysis/${analysisId}/results`);
    return response.data;
  },
  getArticle: async (uuid) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/article/${uuid}`);
    return response.data;
  },
  getGeneratedContent: async (uuid) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/article/${uuid}/step4/content`);
    return response.data;
  },
  getScoring: async (uuid) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/article/${uuid}/scoring`);
    return response.data;
  },
  getWritingPlan: async (uuid) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/article/${uuid}/step4/writing-plan`);
    return response.data;
  },
  getStep4Draft: async (uuid) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/article/${uuid}/step4/draft`);
    return response.data;
  },
  getStep4Citations: async (uuid) => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/article/${uuid}/step4/citations`);
    return response.data;
  },
  getDefaultStyle: async () => {
    const response = await axios.get(`${API_BASE_URL}/api/public/samples/default-style`);
    return response.data;
  },
};
